import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

import { apiRequest } from '../utils/apiHelper';
import '../styles/login.css';
import { showSuccess, showError } from '../utils/toastHelper';

const ForceChangePassword = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { branding, loading: brandingLoading } = useBranding();
  
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [formError, setFormError] = useState('');
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const hasMinLength = formData.newPassword.length >= 8;
  const hasUpper = /[A-Z]/.test(formData.newPassword);
  const hasLower = /[a-z]/.test(formData.newPassword);
  const hasNumber = /\d/.test(formData.newPassword);
  const isPasswordValid = hasMinLength && hasUpper && hasLower && hasNumber;

  // If user doesn't need to change password or is not logged in, redirect away
  React.useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    } else if (!user.shouldChangePassword) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    if (formData.newPassword !== formData.confirmPassword) {
      return setFormError('Yeni şifreler eşleşmiyor.');
    }
    
    if (!isPasswordValid) {
      return setFormError('Lütfen tüm şifre kurallarını karşıladığınızdan emin olun.');
    }

    setLoading(true);
    try {
      await apiRequest('/auth/change-password', {
        method: 'POST',
        data: {
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        }
      });

      showSuccess('Şifreniz başarıyla güncellendi!');
      
      // Update user state to remove the flag
      setUser({
        ...user,
        shouldChangePassword: false
      });
      
      navigate('/dashboard', { replace: true });
    } catch (error) {
      setFormError(error.message || 'Şifre değiştirilirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  if (brandingLoading || !user) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: '#f8f9fc' }}>
        <div className="spinner-border" style={{ color: '#aaa' }} role="status" />
      </div>
    );
  }

  const companyName = branding.name || '';
  const logoSrc = branding.logoUrl || branding.companyLogo || null;
  const accent = branding.brandColor || '#1e293b';
  const accentSoft = accent + '18';
  const heroTitle = branding.heroTitle || '';
  const heroSub = branding.heroSubtitle || branding.tagline || '';

  return (
    <div className="login-page">
      {/* ── Left: Branded Panel ─────────────────────────────── */}
      <div className="login-brand-panel" style={{ '--accent': accent, '--accent-soft': accentSoft }}>
        <div className="login-brand-bg" />
        <div className="login-brand-content">
          {logoSrc && (
            <img src={logoSrc} alt={companyName || 'Logo'} className="login-brand-logo" />
          )}
          {heroTitle && <h1 className="login-brand-title">{heroTitle}</h1>}
          {heroSub && <p className="login-brand-subtitle">{heroSub}</p>}
          {!logoSrc && !heroTitle && companyName && companyName !== 'System Admin Company' && (
            <h1 className="login-brand-title">{companyName}</h1>
          )}
        </div>
      </div>

      {/* ── Right: Form ─────────────────────────────────────── */}
      <div className="login-form-panel">
        <div className="login-form-wrapper">
          {/* Mobile-only header */}
          <div className="login-mobile-header" style={{ '--accent': accent }}>
            {logoSrc ? (
              <img src={logoSrc} alt={companyName || 'Logo'} className="login-mobile-logo" />
            ) : companyName && companyName !== 'System Admin Company' ? (
              <span className="login-mobile-name" style={{ color: accent }}>{companyName}</span>
            ) : null}
          </div>

          <div className="login-form-inner">
            <h2 className="login-form-title">Şifrenizi Değiştirin</h2>
            <p className="login-form-desc text-danger fw-medium mb-3">
              <i className="fas fa-shield-alt me-2" />
              Güvenliğiniz için lütfen devam etmeden önce şifrenizi değiştirin.
            </p>

            {formError && (
              <div className="alert alert-danger d-flex align-items-center mb-4" role="alert" style={{ fontSize: '0.875rem', borderRadius: '10px', backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>
                <i className="fas fa-exclamation-circle me-2" style={{ fontSize: '1.2rem' }} />
                <div>{formError}</div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form mt-2">
              <div className="login-field">
                <label className="login-label" htmlFor="currentPassword">Mevcut Şifre</label>
                <div className="login-input-wrap" style={{ '--accent': accent }}>
                  <i className="fas fa-lock login-input-icon" />
                  <input
                    id="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    name="currentPassword"
                    className="login-input"
                    value={formData.currentPassword}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    placeholder="Mevcut şifreniz"
                  />
                  <button
                    type="button"
                    className="login-eye-btn"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    tabIndex={-1}
                  >
                    <i className={`fas fa-${showCurrentPassword ? 'eye-slash' : 'eye'}`} />
                  </button>
                </div>
              </div>

              <div className="login-field">
                <label className="login-label" htmlFor="newPassword">Yeni Şifre</label>
                <div className="login-input-wrap" style={{ '--accent': accent }}>
                  <i className="fas fa-key login-input-icon" />
                  <input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    name="newPassword"
                    className="login-input"
                    value={formData.newPassword}
                    onChange={handleChange}
                    required
                    minLength="8"
                    disabled={loading}
                    placeholder="Yeni şifreniz"
                  />
                  <button
                    type="button"
                    className="login-eye-btn"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    tabIndex={-1}
                  >
                    <i className={`fas fa-${showNewPassword ? 'eye-slash' : 'eye'}`} />
                  </button>
                </div>
                
                {/* Password Hints */}
                <div className="mt-2" style={{ fontSize: '0.8rem' }}>
                  <div className={`d-flex align-items-center ${hasMinLength ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasMinLength ? 'fa-check-circle' : 'fa-circle'} me-2`} style={{ fontSize: '0.6rem' }} /> En az 8 karakter
                  </div>
                  <div className={`d-flex align-items-center ${hasUpper ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasUpper ? 'fa-check-circle' : 'fa-circle'} me-2`} style={{ fontSize: '0.6rem' }} /> En az 1 büyük harf
                  </div>
                  <div className={`d-flex align-items-center ${hasLower ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasLower ? 'fa-check-circle' : 'fa-circle'} me-2`} style={{ fontSize: '0.6rem' }} /> En az 1 küçük harf
                  </div>
                  <div className={`d-flex align-items-center ${hasNumber ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasNumber ? 'fa-check-circle' : 'fa-circle'} me-2`} style={{ fontSize: '0.6rem' }} /> En az 1 rakam
                  </div>
                </div>
              </div>

              <div className="login-field">
                <label className="login-label" htmlFor="confirmPassword">Yeni Şifre (Tekrar)</label>
                <div className="login-input-wrap" style={{ '--accent': accent }}>
                  <i className="fas fa-check-double login-input-icon" />
                  <input
                    id="confirmPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    className="login-input"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    minLength="8"
                    disabled={loading}
                    placeholder="Yeni şifrenizi tekrar girin"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="login-submit mt-4"
                style={{ '--accent': accent }}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                ) : (
                  <i className="fas fa-save me-2" />
                )}
                Şifreyi Güncelle
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForceChangePassword;
